import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { useWallet } from './SignedWalletProvider'; // Adjust the import path
import ApiKeysManager from './ApiKeys'; // Adjust the import path
import PlanUsage from './PlanUsage'; // Adjust the import path

export const TabsExtended = ({
  children,
  defaultIndex = 0,
  onChange,
  className = "",
}) => {
  const tabs = React.Children.toArray(children);
  const [activeIndex, setActiveIndex] = useState(defaultIndex);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

  const listRef = useRef(null);
  const btnRefs = useRef([]);

  // Ensure the refs array length matches the number of tabs
  btnRefs.current = useMemo(
    () => tabs.map((_, i) => btnRefs.current[i] ?? React.createRef()),
    [tabs.length]
  );

  useEffect(() => {
    const el = btnRefs.current[activeIndex]?.current;
    if (!el) return;
    const { offsetLeft, offsetWidth } = el;
    setIndicatorStyle({ left: offsetLeft, width: offsetWidth });

    // Scroll active tab into view (nice when tabs overflow)
    el.scrollIntoView({ inline: "center", behavior: "smooth", block: "nearest" });
  }, [activeIndex, tabs.length]);

  const setIdx = (i) => {
    setActiveIndex(i);
    onChange?.(i);
  };

  // Keyboard navigation: Left/Right/Home/End
  const onKeyDown = (e) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;

    e.preventDefault();
    const last = tabs.length - 1;

    if (e.key === "ArrowRight") setIdx(activeIndex === last ? 0 : activeIndex + 1);
    if (e.key === "ArrowLeft") setIdx(activeIndex === 0 ? last : activeIndex - 1);
    if (e.key === "Home") setIdx(0);
    if (e.key === "End") setIdx(last);

    // Focus the new active button for a11y
    requestAnimationFrame(() => {
      btnRefs.current[activeIndex]?.current?.focus();
    });
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Tablist */}
      <div
        ref={listRef}
        role="tablist"
        aria-orientation="horizontal"
        onKeyDown={onKeyDown}
        className="
          relative
          flex gap-2
          overflow-x-auto
          rounded-t-2xl
          bg-white/50 dark:bg-white/5
          p-2
          backdrop-blur
          ring-1 ring-black/5 dark:ring-white/10
        "
      >
        {/* Animated underline/indicator */}
        <div
          aria-hidden="true"
          className="
            pointer-events-none absolute bottom-0 h-0.5
            bg-gradient-to-r from-cyan-400 via-indigo-400 to-fuchsia-400
            transition-[left,width] duration-300 ease-out
          "
          style={{ left: indicatorStyle.left, width: indicatorStyle.width }}
        />

        {tabs.map((tab, idx) => {
          const title = tab.props.title ?? `Tab ${idx + 1}`;
          const isActive = idx === activeIndex;
          const id = `tab-${idx}`;
          const panelId = `panel-${idx}`;

          return (
            <button
              key={id}
              ref={btnRefs.current[idx]}
              id={id}
              role="tab"
              aria-selected={isActive}
              aria-controls={panelId}
              tabIndex={isActive ? 0 : -1}
              onClick={() => setIdx(idx)}
              className={[
                "relative",
                "px-6 py-3 text-base md:text-lg font-semibold",
                "rounded-xl",
                "transition-colors duration-200",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/70",
                isActive
                  ? "text-white dark:text-white"
                  : "text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white",
                isActive
                  ? "bg-gradient-to-b from-white/70 to-white/40 dark:from-white/10 dark:to-white/5"
                  : "bg-white/40 hover:bg-white/60 dark:bg-white/0 dark:hover:bg-white/5",
              ].join(" ")}
            >
              {/* Subtle glow when active */}
              {isActive && (
                <span
                  className="
                    pointer-events-none absolute inset-0 -z-10
                    rounded-xl shadow-[0_0_24px_-6px_rgba(56,189,248,0.35)]
                    dark:shadow-[0_0_24px_-6px_rgba(129,140,248,0.4)]
                  "
                />
              )}
              {title}
            </button>
          );
        })}
      </div>

      {/* Panels */}
      <div
        className="
          rounded-b-2xl
          bg-white/60 dark:bg-white/[0.03]
          p-4 md:p-6
          ring-1 ring-black/5 dark:ring-white/10
        "
      >
        {tabs.map((tab, idx) => {
          const isActive = idx === activeIndex;
          const id = `panel-${idx}`;
          const tabId = `tab-${idx}`;
          return (
            <div
              key={id}
              id={id}
              role="tabpanel"
              aria-labelledby={tabId}
              hidden={!isActive}
              className="animate-fadeIn"
            >
              {isActive && tab}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// const CodeInstructions = ({ botUsername, code, chainId, deepLink }) => { 
//   console.log("CodeInstructions props:", { botUsername, code, chainId, deepLink });
//   return (
//     <>
//       <div className="mt-4 p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
//         <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 mt-0">
//           Finish Linking in Discord code: {code ? code : "—"} {chainId ? `(Chain ID: ${chainId})` : ""}
//         </h3>

//         {/* Deep Link */}
//         <div className="mt-4 flex items-center gap-2">
//           <a
//             href={deepLink || "#"}
//             rel="noreferrer"
//             className={`px-4 py-2 rounded text-white transition-colors ${
//               deepLink
//                 ? "bg-emerald-600 hover:bg-emerald-700"
//                 : "bg-gray-400 cursor-not-allowed"
//             }`}
//           >
//             Open Discord &amp; Link
//           </a>
//           {deepLink && (
//             <button
//               type="button"
//               onClick={() => copy(deepLink)}
//               className="px-3 py-2 rounded bg-gray-300 dark:bg-gray-600 text-gray-900 dark:text-gray-100 hover:bg-gray-400 dark:hover:bg-gray-500"
//             >
//               Copy Deep Link
//             </button>
//           )}
//         </div>

//       </div>
//     </>
//   )
// };


// export const LinkDiscordAccount = ({ walletAddress, botUsername, code, deepLink, config }) => {
//   const { jwtToken, network } = useWallet();
//   const [username, setUsername] = useState("");
//   const [loading, setLoading] = useState(false);
//   const [message, setMessage] = useState(null);
//   const [result, setResult] = useState(null); // holds API response on success
//   const [codeState, setCodeState] = useState(code);
//   const [deepLinkState, setDeepLinkState] = useState(deepLink);

//   useEffect(() => {
//     setCodeState(code);
//     setDeepLinkState(deepLink);
//   }, [code, deepLink]);


//   const copy = async (value) => {
//     try {
//       await navigator.clipboard.writeText(value);
//       setMessage({ type: "success", text: "Copied to clipboard." });
//     } catch {
//       setMessage({ type: "error", text: "Copy failed. Please copy manually." });
//     }
//   };

//   return (
//     <>
//     {code ? (<>
//       <CodeInstructions botUsername={botUsername || "deeporbxyz_bot"} code={codeState} deepLink={deepLinkState} chainId={network.chainId || "1"} /> 
//     </>) : (<>


//     </>)}

    
//     </>
//   );
// };



/**
 * Fixed-size card list:
 * - Card width is constant (w-72 ~ 288px)
 * - Aspect ratio is 1/2 (width : height) → taller than wide
 * - Wraps naturally based on viewport (1 per row on small, up to 8 per row on very wide)
 */
// export const FeatureList = ({ data }) => {


//   const navigate = useNavigate();

//   if (!data || data.length === 0) {
//     return <div className="text-gray-500 dark:text-gray-400">No features found.</div>;
//   }

//   const navigateToFeature = (featureId) => {
//     // Implement navigation logic here, e.g., using React Router's useNavigate
//     console.log("Navigating to feature:", featureId);
//     navigate(`/feature/${featureId}`);
//   };

//   return (
//     <ul
//       role="list"
//       className="
//         flex flex-wrap gap-4
//         justify-center md:justify-start
//         mx-auto
//         max-w-screen-2xl
//       "
//     >
//       {data.map((item, i) => (
//         <li key={i} className="list-none" onClick={() => navigateToFeature(item.featureId)}>
//           {/* Card container with fixed width and locked aspect ratio */}
//           <div
//             className={`relative w-72 aspect-[2/1]
//               rounded-2xl overflow-hidden
//               bg-white dark:bg-gray-600          
//               shadow-md hover:brightness-125
//               hover:shadow-lg transition-shadow ${
//                     item.featureActive
//                       ? "dark:bg-green-900 text-green-400"
//                       : "dark:bg-gray-600 dark:text-gray-400"
//                   }`}
//           >
//             {/* Content layer */}
//             <div className="absolute inset-0 p-4 flex flex-col">
//               <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-0 mb-0">
//                 {item.title}
//               </h3>
//               <span className="mt-0 text-sm text-gray-700 dark:text-gray-300">
//                 {item.description}
//               </span>
//               {item.featureActive && !item.userEnabled && (
//                 <span className="mt-0 text-sm text-yellow-400 font-semibold">
//                   Subscribe to use this feature
//                 </span>
//               )}
//               {item.featureActive && item.userEnabled && (
//                 <span className="mt-0 text-sm text-green-400 font-semibold">
//                   Feature is enabled for you
//                 </span>
//               )}
//               {!item.featureActive && (
//                 <span className="mt-0 text-sm text-gray-400 font-semibold">
//                   Feature available soon
//                 </span>
//               )}
//               {/* <div className="flex-grow" /> */}
              
//             </div>
//             {/* Focus ring (for a11y when the card becomes focusable in future) */}
//             <span className="absolute inset-0 rounded-2xl ring-inset focus-within:ring-2 focus-within:ring-cyan-400/70" />
//           </div>
//         </li>
//       ))}
//     </ul>
//   );
// };

export const UserDashboard = ({config}) => {
  const { jwtToken, network, account } = useWallet();
  const [deeporbAccount, setDeeporbAccount] = useState(null);
  // const [deepLink, setDeepLink] = useState(null);
  // const [code, setCode] = useState(null);
  // const [features, setFeatures] = useState([]);

  // 1. Create a ref to track if the API call has been initiated.
  const fetchInitiated = useRef(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${config.apiBaseUrl}/user`, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
        });
        
        if (!response.ok) {
          // Handle HTTP errors from the backend
          const errorData = await response.json().catch(() => ({})); // Gracefully handle non-json errors
          throw new Error(errorData.detail || `Failed to fetch user data with status ${response.status}`);
        }
        
        const userData = await response.json();
        console.log("User data fetched successfully:", userData);
        // setFeatures(userData.features || []);
        if (userData && userData.isActive) {
          setDeeporbAccount(userData);
        }


      } catch (error) {
        console.error("Error fetching user data:", error);
        setDeeporbAccount(null);
      }
    };

    // 2. Add a robust guard clause.
    // This prevents the fetch if dependencies are not ready OR if the fetch has already been started.
    if (!config?.apiBaseUrl || !jwtToken || !network || fetchInitiated.current) {
      console.log("Skipping fetch: Dependencies not ready or fetch already initiated.");
      return;
    }

    // 3. Set the ref to true immediately before making the API call.
    // This acts as our "gate" to prevent subsequent runs.
    fetchInitiated.current = true;
    console.log("Dependencies are ready, initiating user fetch.");
    fetchUser();
    
  // The dependency array is correct. We want the effect to re-evaluate if these change.
  }, [config, jwtToken, network]);

  return (  
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">User Dashboard</h1>
      {/* <div className='mt-0'>Welcome to your dashboard! Here you can manage your settings and view your activity.</div>
      {network && (
        <div>Your current network is: <strong>{network.name}</strong></div>
      )}
      <div>Your connected MetaMask account is: <span className='font-mono text-yellow-300'>{account}</span></div>
      {account && <>Your plan is <strong>{deeporbAccount?.plan?.tier || "FREE"}</strong></>} */}
      {deeporbAccount === null ? (
        <> 
          Link your account to get started with Deeporb.
        </>
      ) : (
        <div className="mt-2 p-2 border border-gray-600 rounded-lg bg-gray-800">
          <TabsExtended>
            <div title="Usage">
              <PlanUsage config={config} />
            </div>
            <div title="Access">
              <ApiKeysManager config={config} />
            </div>
          </TabsExtended>
        </div>
      )}
    </div>
  );
}